import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { AlertTriangle } from "lucide-react";

export default function Tailings360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/tailings/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Tailings360"
                title="Tailings Storage Facilities & TARP"
                description="Instrumented tailings facilities, piezometers, freeboard, seepage, stability factors, trigger-action-response plans and independent technical review."
            />
            <KpiGrid kpis={data?.kpis || []} accent="red" cols={4} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {(data?.facilities || []).map((f) => (
                    <div key={f.id} className={`kcc-panel kcc-${f.tarp_status === "Red" ? "red" : f.tarp_status === "Amber" ? "amber" : "green"}-accent p-5`} data-testid={`facility-${f.code}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="kcc-label">{f.code}</div>
                                <div className="font-display text-lg font-semibold mt-1 text-white">{f.name}</div>
                                <div className="text-[11px] kcc-text-silver">{f.type} · EoR: {f.engineer_of_record}</div>
                            </div>
                            <StatusChip status={f.tarp_status === "Red" ? "critical" : f.tarp_status === "Amber" ? "warning" : "normal"}>TARP {f.tarp_status}</StatusChip>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                            <div><div className="kcc-label">Capacity</div><div className="text-white mt-0.5">{f.capacity_mm3} Mm³</div></div>
                            <div><div className="kcc-label">Current fill</div><div className="text-white mt-0.5">{f.current_fill_mm3} Mm³</div></div>
                            <div><div className="kcc-label">Freeboard</div><div className="text-white mt-0.5">{f.freeboard_m} m</div></div>
                            <div><div className="kcc-label">Beach</div><div className="text-white mt-0.5">{f.beach_length_m} m</div></div>
                            <div><div className="kcc-label">Seepage</div><div className="text-white mt-0.5">{f.seepage_lpm} L/min</div></div>
                            <div><div className="kcc-label">Factor of Safety</div><div className="text-white mt-0.5">{f.stability_factor}</div></div>
                        </div>
                    </div>
                ))}
            </div>

            <Panel title="Piezometer Readings" subtitle="Automated + manual · trend against trigger level" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "tag", label: "Piezometer" },
                        { key: "facility", label: "Facility" },
                        { key: "depth_m", label: "Depth (m)" },
                        { key: "reading_m", label: "Reading (m)" },
                        { key: "trigger_m", label: "Trigger (m)" },
                        { key: "trend_7d", label: "7-day Δ", render: (r) => <span className={r.trend_7d > 0 ? "text-kcc-amber" : "kcc-text-silver"}>{r.trend_7d > 0 ? "+" : ""}{r.trend_7d}</span> },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                    ]}
                    rows={data?.piezos || []}
                    testid="piezo-table"
                />
            </Panel>
        </div>
    );
}
